/**
 * DonationService — state machine + persistence for donations.
 *
 * Wraps WalletService with a simple state machine + local history so
 * the UI just consumes progress events. No wallet secrets are ever
 * stored. The only local persistence is a short list of past donation
 * hashes so the user can find their tx again.
 */

import {
  connectWallet,
  ensureBSC,
  sendDonation,
  waitForReceipt,
} from "./walletService";

export const DONATION_STATES = Object.freeze({
  IDLE: "idle",
  CONNECTING: "connecting",
  SWITCHING_CHAIN: "switching-chain",
  AWAITING_SIGNATURE: "awaiting-signature",
  PENDING: "pending",
  SUCCESS: "success",
  ERROR: "error",
  CANCELLED: "cancelled",
});

const HISTORY_KEY = "esquiada.donations.v1";

const readHistory = () => {
  try {
    return JSON.parse(localStorage.getItem(HISTORY_KEY) || "[]");
  } catch {
    return [];
  }
};
const writeHistory = (arr) => {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(arr.slice(-50)));
  } catch {
    /* ignore quota errors */
  }
};

export const getDonationHistory = () => readHistory();

const isUserRejection = (err) => {
  const code = err?.code || err?.data?.code;
  const msg = String(err?.message || "").toLowerCase();
  return (
    code === 4001 ||
    code === "ACTION_REJECTED" ||
    msg.includes("user rejected") ||
    msg.includes("user denied")
  );
};

/**
 * Orchestrate a full donation flow. `onState` receives progress updates
 * as `{ state, data }` where `data` may include tx hash, receipt, error.
 */
export const donateWithWallet = async ({ wallet, amount, onState }) => {
  const emit = (state, data = {}) => onState?.({ state, data });

  try {
    // 1. Connect
    emit(DONATION_STATES.CONNECTING);
    const { provider, account } = await connectWallet(wallet.provider);

    // 2. Ensure BSC
    emit(DONATION_STATES.SWITCHING_CHAIN);
    await ensureBSC(provider);

    // 3. Send
    emit(DONATION_STATES.AWAITING_SIGNATURE, { account });
    const hash = await sendDonation({ provider, from: account, amount });

    // 4. Persist optimistically
    const record = {
      hash,
      amount: String(amount),
      from: account,
      wallet: wallet.name,
      created_at: new Date().toISOString(),
    };
    writeHistory([...readHistory(), record]);

    emit(DONATION_STATES.PENDING, { hash, account, record });

    // 5. Wait for receipt (non-blocking failure OK)
    const receipt = await waitForReceipt(provider, hash);
    if (!receipt) {
      // Timeout: leave as pending but resolve so UI can show "still pending"
      emit(DONATION_STATES.PENDING, { hash, account, record, timeout: true });
      return { hash, receipt: null, timeout: true };
    }

    // 0x1 = success, 0x0 = failed
    const success = receipt.status === "0x1" || receipt.status === 1;
    if (!success) {
      emit(DONATION_STATES.ERROR, { hash, receipt, error: "Transacción fallida en la red." });
      return { hash, receipt, success: false };
    }
    emit(DONATION_STATES.SUCCESS, { hash, receipt, record });
    return { hash, receipt, success: true };
  } catch (err) {
    if (isUserRejection(err)) {
      emit(DONATION_STATES.CANCELLED, { error: "Firma cancelada." });
      return { cancelled: true };
    }
    emit(DONATION_STATES.ERROR, { error: err?.message || "Error inesperado" });
    return { error: err };
  }
};
